import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { zodResolver } from "@hookform/resolvers/zod"
import { CreditCard, Loader2 } from "lucide-react"
import { useForm } from "react-hook-form"

import type { CreditCardFormData } from "../../schemas"
import { creditCardSchema } from "../../schemas"

interface CreditCardFormProps {
  onSubmit: (data: CreditCardFormData) => Promise<void>
  isLoading?: boolean
  showInstallments?: boolean
  maxInstallments?: number
  onInstallmentsChange?: (installments: number) => void
}

export function CreditCardForm({
  onSubmit,
  isLoading = false,
  showInstallments = false,
  maxInstallments = 12,
  onInstallmentsChange,
}: CreditCardFormProps) {
  const form = useForm<CreditCardFormData>({
    resolver: zodResolver(creditCardSchema),
    defaultValues: {
      number: "",
      holderName: "",
      expMonth: "",
      expYear: "",
      cvv: "",
    },
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Número do cartão</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder="0000 0000 0000 0000"
                    maxLength={19}
                    {...field}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, "")
                      field.onChange(value)
                    }}
                  />
                  <CreditCard className="text-muted-foreground absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="holderName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome do titular</FormLabel>
              <FormControl>
                <Input placeholder="Nome como está no cartão" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-3 gap-3">
          <FormField
            control={form.control}
            name="expMonth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mês</FormLabel>
                <FormControl>
                  <Input placeholder="MM" maxLength={2} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="expYear"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ano</FormLabel>
                <FormControl>
                  <Input placeholder="AA" maxLength={4} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cvv"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CVV</FormLabel>
                <FormControl>
                  <Input placeholder="123" maxLength={4} type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {showInstallments && (
          <div>
            <label className="text-sm font-medium">Parcelas</label>
            <Select defaultValue="1" onValueChange={v => onInstallmentsChange?.(parseInt(v))}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: maxInstallments }, (_, i) => i + 1).map(n => (
                  <SelectItem key={n} value={String(n)}>
                    {n}x {n === 1 ? "à vista" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processando...
            </>
          ) : (
            "Pagar com cartão"
          )}
        </Button>
      </form>
    </Form>
  )
}
